import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  BarChart3, Users, FileText, TrendingUp, Award, Building2, 
  Calendar, Target, Briefcase, ChevronUp, ChevronDown, Minus
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [overview, setOverview] = useState(null);
  const [scores, setScores] = useState(null);
  const [weeklyTrend, setWeeklyTrend] = useState(null);
  const [cvsByPeriod, setCvsByPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchMetrics();
  }, [user, navigate]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const [overviewRes, scoresRes, trendRes, periodsRes] = await Promise.all([
        api.get('/metrics/overview'),
        api.get('/metrics/scores'),
        api.get('/metrics/scores/weekly-trend'),
        api.get('/metrics/cvs/by-period')
      ]);
      
      setOverview(overviewRes.data);
      setScores(scoresRes.data);
      setWeeklyTrend(trendRes.data);
      setCvsByPeriod(periodsRes.data);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError(language === 'pt' ? 'Erro ao carregar métricas' : 'Error loading metrics');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    return 'text-[#1E3C3C]';
  };

  const getScoreBorderColor = (score) => {
    return 'border-[#1E3C3C]';
  };

  const getTrendIcon = (current, previous) => {
    if (!previous || previous === 0) return <Minus className="w-4 h-4 text-gray-400" />;
    if (current > previous) return <ChevronUp className="w-4 h-4 text-green-500" />;
    if (current < previous) return <ChevronDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3C3C]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  const t = {
    title: language === 'pt' ? 'Dashboard de Métricas' : 'Metrics Dashboard',
    overview: language === 'pt' ? 'Visão Geral' : 'Overview',
    totalCvs: language === 'pt' ? 'Total de CVs' : 'Total CVs',
    thisWeek: language === 'pt' ? 'Esta Semana' : 'This Week',
    thisMonth: language === 'pt' ? 'Este Mês' : 'This Month',
    byTemplate: language === 'pt' ? 'Por Template' : 'By Template',
    topCompetencies: language === 'pt' ? 'Top Competências' : 'Top Competencies',
    topClients: language === 'pt' ? 'Top Clientes' : 'Top Clients',
    scores: language === 'pt' ? 'Scores de Avaliação' : 'Evaluation Scores',
    avgTechnical: language === 'pt' ? 'Média Teste Técnico' : 'Avg Technical Test',
    avgInterview: language === 'pt' ? 'Média Entrevista' : 'Avg Interview',
    weekly: language === 'pt' ? 'Semanal' : 'Weekly',
    monthly: language === 'pt' ? 'Mensal' : 'Monthly',
    quarterly: language === 'pt' ? 'Trimestral' : 'Quarterly',
    weeklyTrend: language === 'pt' ? 'Tendência Semanal (12 semanas)' : 'Weekly Trend (12 weeks)',
    cvsByPeriod: language === 'pt' ? 'CVs por Período' : 'CVs by Period',
    evaluated: language === 'pt' ? 'avaliados' : 'evaluated',
    cvs: 'CVs',
    noData: language === 'pt' ? 'Sem dados' : 'No data'
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {t.title}
          </h1>
          <p className="text-[#64748b] mt-1">
            {language === 'pt' ? 'Estatísticas e análises da plataforma' : 'Platform statistics and analytics'}
          </p>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-l-4 border-l-[#1E3C3C]" data-testid="total-cvs-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b]">{t.totalCvs}</p>
                  <p className="text-3xl font-bold text-[#0f172a]">{overview?.total_cvs || 0}</p>
                </div>
                <div className="p-3 bg-[#1E3C3C]/10 rounded-full">
                  <FileText className="w-6 h-6 text-[#1E3C3C]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500" data-testid="cvs-week-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b]">{t.thisWeek}</p>
                  <p className="text-3xl font-bold text-[#0f172a]">{overview?.cvs_this_week || 0}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-full">
                  <Calendar className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500" data-testid="cvs-month-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b]">{t.thisMonth}</p>
                  <p className="text-3xl font-bold text-[#0f172a]">{overview?.cvs_this_month || 0}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-full">
                  <TrendingUp className="w-6 h-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500" data-testid="template-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b]">{t.byTemplate}</p>
                  <div className="flex gap-4 mt-1">
                    <span className="text-sm">
                      <span className="font-bold text-[#0f172a]">{overview?.cvs_by_template?.outsourcing || 0}</span>
                      <span className="text-[#64748b] ml-1">Out.</span>
                    </span>
                    <span className="text-sm">
                      <span className="font-bold text-[#0f172a]">{overview?.cvs_by_template?.perm || 0}</span>
                      <span className="text-[#64748b] ml-1">Perm</span>
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-orange-50 rounded-full">
                  <Briefcase className="w-6 h-6 text-orange-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scores Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Weekly Scores */}
          <Card data-testid="weekly-scores-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#1E3C3C]" />
                {t.weekly}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg bg-white border ${getScoreBorderColor(scores?.week?.avg_technical || 0)}`}>
                  <p className="text-sm text-[#64748b]">{t.avgTechnical}</p>
                  <p className={`text-2xl font-bold ${getScoreColor(scores?.week?.avg_technical || 0)}`}>
                    {scores?.week?.avg_technical || 0}%
                  </p>
                  <p className="text-xs text-[#94a3b8] mt-1">
                    {scores?.week?.count_technical || 0} {t.evaluated}
                  </p>
                </div>
                <div className={`p-4 rounded-lg bg-white border ${getScoreBorderColor(scores?.week?.avg_interview || 0)}`}>
                  <p className="text-sm text-[#64748b]">{t.avgInterview}</p>
                  <p className={`text-2xl font-bold ${getScoreColor(scores?.week?.avg_interview || 0)}`}>
                    {scores?.week?.avg_interview || 0}%
                  </p>
                  <p className="text-xs text-[#94a3b8] mt-1">
                    {scores?.week?.count_interview || 0} {t.evaluated}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Scores */}
          <Card data-testid="monthly-scores-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-500" />
                {t.monthly}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg bg-white border ${getScoreBorderColor(scores?.month?.avg_technical || 0)}`}>
                  <p className="text-sm text-[#64748b]">{t.avgTechnical}</p>
                  <p className={`text-2xl font-bold ${getScoreColor(scores?.month?.avg_technical || 0)}`}>
                    {scores?.month?.avg_technical || 0}%
                  </p>
                  <p className="text-xs text-[#94a3b8] mt-1">
                    {scores?.month?.count_technical || 0} {t.evaluated}
                  </p>
                </div>
                <div className={`p-4 rounded-lg bg-white border ${getScoreBorderColor(scores?.month?.avg_interview || 0)}`}>
                  <p className="text-sm text-[#64748b]">{t.avgInterview}</p>
                  <p className={`text-2xl font-bold ${getScoreColor(scores?.month?.avg_interview || 0)}`}>
                    {scores?.month?.avg_interview || 0}%
                  </p>
                  <p className="text-xs text-[#94a3b8] mt-1">
                    {scores?.month?.count_interview || 0} {t.evaluated}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quarterly Scores */}
          <Card data-testid="quarterly-scores-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-orange-500" />
                {t.quarterly}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg bg-white border ${getScoreBorderColor(scores?.quarter?.avg_technical || 0)}`}>
                  <p className="text-sm text-[#64748b]">{t.avgTechnical}</p>
                  <p className={`text-2xl font-bold ${getScoreColor(scores?.quarter?.avg_technical || 0)}`}>
                    {scores?.quarter?.avg_technical || 0}%
                  </p>
                  <p className="text-xs text-[#94a3b8] mt-1">
                    {scores?.quarter?.count_technical || 0} {t.evaluated}
                  </p>
                </div>
                <div className={`p-4 rounded-lg bg-white border ${getScoreBorderColor(scores?.quarter?.avg_interview || 0)}`}>
                  <p className="text-sm text-[#64748b]">{t.avgInterview}</p>
                  <p className={`text-2xl font-bold ${getScoreColor(scores?.quarter?.avg_interview || 0)}`}>
                    {scores?.quarter?.avg_interview || 0}%
                  </p>
                  <p className="text-xs text-[#94a3b8] mt-1">
                    {scores?.quarter?.count_interview || 0} {t.evaluated}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Trend Chart */}
        <Card className="mb-8" data-testid="weekly-trend-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#1E3C3C]" />
              {t.weeklyTrend}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <div className="flex gap-2 min-w-max pb-4">
                {weeklyTrend?.weeks?.map((week, index) => (
                  <div 
                    key={`week-${week.week || index}`} 
                    className="flex flex-col items-center p-3 bg-white border border-[#e2e8f0] rounded-lg min-w-[80px]"
                  >
                    <span className="text-xs text-[#64748b] mb-2">{week.week}</span>
                    <div className="flex flex-col gap-1 text-center">
                      <span className={`text-sm font-semibold ${getScoreColor(week.avg_technical)}`}>
                        T: {week.avg_technical}%
                      </span>
                      <span className={`text-sm font-semibold ${getScoreColor(week.avg_interview)}`}>
                        E: {week.avg_interview}%
                      </span>
                      <span className="text-xs text-[#94a3b8]">
                        {week.total_cvs} CVs
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-6 mt-4 text-sm text-[#64748b]">
              <span>T = {language === 'pt' ? 'Teste Técnico' : 'Technical Test'}</span>
              <span>E = {language === 'pt' ? 'Entrevista' : 'Interview'}</span>
            </div>
          </CardContent>
        </Card>

        {/* Top Competencies & Clients */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Competencies */}
          <Card data-testid="top-competencies-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#1E3C3C]" />
                {t.topCompetencies}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {overview?.top_competencies?.length > 0 ? (
                <div className="space-y-3">
                  {overview.top_competencies.map((comp, index) => (
                    <div key={`top-comp-${comp.name || index}`} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-[#1E3C3C]/10 flex items-center justify-center text-xs font-medium text-[#1E3C3C]">
                          {index + 1}
                        </span>
                        <span className="text-sm text-[#0f172a]">{comp.name}</span>
                      </div>
                      <span className="text-sm font-medium text-[#1E3C3C]">{comp.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#64748b] text-center py-4">{t.noData}</p>
              )}
            </CardContent>
          </Card>

          {/* Top Clients */}
          <Card data-testid="top-clients-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                {t.topClients}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {overview?.top_clients?.length > 0 ? (
                <div className="space-y-3">
                  {overview.top_clients.map((client, index) => (
                    <div key={`top-client-${client.name || index}`} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center text-xs font-medium text-blue-500">
                          {index + 1}
                        </span>
                        <span className="text-sm text-[#0f172a]">{client.name}</span>
                      </div>
                      <span className="text-sm font-medium text-blue-500">{client.count} CVs</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#64748b] text-center py-4">{t.noData}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
